import apiClient from './api';

const lifeDeclarationReviewService = {
  /**
   * List all life declarations pending admin review (lightweight, no images)
   * @param {Object} params - Query params (page, page_size)
   * @returns {Promise}
   */
  async listReviews(params = {}) {
    try {
      const response = await apiClient.get('/admin/life-declaration-reviews/', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error);
    }
  },

  /**
   * Get full detail of a single declaration for review, including image URLs
   * @param {string} declarationId
   * @returns {Promise}
   */
  async getReview(declarationId) {
    try {
      const response = await apiClient.get(`/admin/life-declaration-reviews/${declarationId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error);
    }
  },

  /**
   * Fetch a review image as a blob (authenticated proxy)
   * image_key: 'submitted_selfie' | 'primary' | 'semi_primary' | 'secondary_1' | 'secondary_2'
   * @param {string} declarationId
   * @param {string} imageKey
   * @returns {Promise<{success: boolean, data?: {blobUrl: string, mimeType: string}, error?: string}>}
   */
  async getImageBlob(declarationId, imageKey) {
    try {
      const response = await apiClient.get(
        `/admin/life-declaration-reviews/${declarationId}/images/${imageKey}/`,
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(response.data);
      return { success: true, data: { blobUrl, mimeType: response.data.type || 'image/jpeg' } };
    } catch (error) {
      return this.handleError(error);
    }
  },

  /**
   * Approve or reject a declaration after manual image review
   * @param {string} declarationId
   * @param {Object} data
   * @param {string} data.decision - 'approved' | 'rejected'
   * @param {string} [data.notes]
   * @returns {Promise}
   */
  async submitReview(declarationId, data) {
    try {
      const response = await apiClient.post(
        `/admin/life-declaration-reviews/${declarationId}/review/`,
        data
      );
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error);
    }
  },

  handleError(error) {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 500) return { success: false, error: 'Server error. Please try again later.' };
      if (status === 403) return { success: false, error: 'You do not have permission to perform this action.' };
      if (status === 404) return { success: false, error: 'Declaration not found.' };
      if (status === 401) return { success: false, error: 'Authentication required.' };
      if (data) {
        if (typeof data === 'string' && data.includes('<!doctype html>'))
          return { success: false, error: `Server error (${status}). Please try again later.` };
        if (typeof data === 'string') return { success: false, error: data };
        if (data.error) return { success: false, error: data.error };
        if (data.detail) return { success: false, error: data.detail };
        if (data.message) return { success: false, error: data.message };
        const firstKey = Object.keys(data)[0];
        if (firstKey) {
          const val = data[firstKey];
          return { success: false, error: Array.isArray(val) ? val[0] : String(val) };
        }
      }
    } else if (error.request) {
      return { success: false, error: 'Unable to connect to server. Please check your connection.' };
    }
    return { success: false, error: error.message || 'An unexpected error occurred.' };
  },
};

export default lifeDeclarationReviewService;
